import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Stack,
    CircularProgress, Alert, Tooltip, IconButton, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip,
    Dialog, DialogActions, DialogContent, DialogTitle, Snackbar,
    InputAdornment, Autocomplete, Select, MenuItem, FormControl, InputLabel,
    DialogContentText, Divider
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// --- INTERFACES ---
interface Campaign { id: number; name: string; traffic_source_id: number; }
interface TrafficSource { id: number; name: string; api_connection_id?: number | null; }
interface ApiConnection { id: number; name: string; api_adapter_key: string; }
interface AdapterManifest { key: string; name: string; supported_targets: { key: string, label: string }[]; }
interface RuleCondition { id?: number; tracker_dimension: string; target_entity_type: string; }
interface OptimizationRule { id: number; name: string; campaign_id: number; campaign_name?: string; profit_threshold: number; is_active: boolean; last_run?: string; conditions: RuleCondition[]; }

const TRACKER_DIMENSIONS = [
    'sub_id1', 'sub_id2', 'sub_id3', 'sub_id4', 'sub_id5', 
    'sub_id6', 'sub_id7', 'sub_id8', 'sub_id9', 'sub_id10',
    'country_code', 'region_name', 'city_name', 'isp', 'device_type', 'os_name', 'browser_name'
];

const initialFormState: Partial<OptimizationRule> = {
    name: '',
    campaign_id: undefined,
    profit_threshold: -1,
    conditions: [{ tracker_dimension: '', target_entity_type: '' }]
};

// --- COMPONENTE PRINCIPAL ---
const OptimizerPage: React.FC = () => {
    const [error, setError] = useState<string | null>(null);
    const [rules, setRules] = useState<OptimizationRule[]>([]);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [trafficSources, setTrafficSources] = useState<TrafficSource[]>([]);
    const [connections, setConnections] = useState<ApiConnection[]>([]);
    const [adapterManifests, setAdapterManifests] = useState<AdapterManifest[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingRule, setEditingRule] = useState<Partial<OptimizationRule> | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean, id: number | null }>({ open: false, id: null });
    const [resetConfirm, setResetConfirm] = useState<{ open: boolean, id: number | null }>({ open: false, id: null });

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [rulesRes, campaignsRes, tsRes, connRes, manifestsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/optimizer/rules`),
                axios.get(`${API_BASE_URL}/campaigns`),
                axios.get(`${API_BASE_URL}/traffic-sources`),
                axios.get(`${API_BASE_URL}/connections`),
                axios.get(`${API_BASE_URL}/optimizer/adapter-manifests`)
            ]);
            setRules(rulesRes.data);
            setCampaigns(campaignsRes.data);
            setTrafficSources(tsRes.data);
            setConnections(connRes.data);
            setAdapterManifests(manifestsRes.data);
        } catch (err: any) {
            setNotification({ type: 'error', message: 'Error al cargar los datos: ' + (err.response?.data?.message || err.message) });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenForm = (rule: Partial<OptimizationRule> | null = null) => {
        setEditingRule(rule || initialFormState);
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setEditingRule(null);
    };

    const handleSaveRule = async (ruleData: Partial<OptimizationRule>) => {
        const method = ruleData.id ? 'put' : 'post';
        const url = ruleData.id ? `${API_BASE_URL}/optimizer/rules/${ruleData.id}` : `${API_BASE_URL}/optimizer/rules`;
        try {
            await axios({ method, url, data: ruleData });
            setNotification({ type: 'success', message: `Regla "${ruleData.name}" guardada con éxito.` });
            handleCloseForm();
            fetchData();
        } catch (err: any) {
            setNotification({ type: 'error', message: 'Error al guardar la regla: ' + (err.response?.data?.message || err.message) });
        }
    };

    const handleDeleteRule = async () => {
        if (!deleteConfirm.id) return;
        try {
            await axios.delete(`${API_BASE_URL}/optimizer/rules/${deleteConfirm.id}`);
            setNotification({ type: 'success', message: 'Regla eliminada.' });
            fetchData();
        } catch (err: any) {
            setNotification({ type: 'error', message: 'Error al eliminar la regla: ' + (err.response?.data?.message || err.message) });
        } finally {
            setDeleteConfirm({ open: false, id: null });
        }
    };

    const handleToggleRuleStatus = async (ruleId: number) => {
        try {
            await axios.post(`${API_BASE_URL}/optimizer/rules/${ruleId}/toggle`);
            setNotification({ type: 'success', message: `Estado de la regla actualizado.` });
            fetchData();
        } catch (err: any) {
            setNotification({ type: 'error', message: 'Error al cambiar el estado: ' + (err.response?.data?.message || err.message) });
        }
    };
    
    const handleResetMemory = async () => {
        if (!resetConfirm.id) return;
        try {
            await axios.post(`${API_BASE_URL}/optimizer/rules/${resetConfirm.id}/reset-memory`);
            setNotification({ type: 'success', message: 'La memoria de la regla ha sido reseteada.' });
        } catch (err: any) {
            setNotification({ type: 'error', message: 'Error al resetear la memoria: ' + (err.response?.data?.message || err.message) });
        } finally {
            setResetConfirm({ open: false, id: null });
        }
    };

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Snackbar open={!!notification} autoHideDuration={4000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>{notification?.message}</Alert>
            </Snackbar>
            
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                    <SmartToyIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                    Motor de Optimización
                </Typography>
                <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => handleOpenForm()}>
                    Crear Nueva Regla
                </Button>
            </Stack>

            {isFormOpen && (
                <RuleForm
                    open={isFormOpen}
                    onClose={handleCloseForm}
                    onSave={handleSaveRule}
                    rule={editingRule}
                    campaigns={campaigns}
                    trafficSources={trafficSources}
                    connections={connections}
                    adapterManifests={adapterManifests}
                />
            )}

            <RulesTable
                rules={rules.map(r => ({...r, campaign_name: campaigns.find(c => c.id === r.campaign_id)?.name }))}
                onEdit={handleOpenForm}
                onDelete={(id) => setDeleteConfirm({ open: true, id })}
                onToggleStatus={handleToggleRuleStatus}
                onResetMemory={(id) => setResetConfirm({ open: true, id })}
            />
            
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null })}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent><DialogContentText>¿Estás seguro de que quieres eliminar esta regla?</DialogContentText></DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, id: null })}>Cancelar</Button>
                    <Button onClick={handleDeleteRule} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={resetConfirm.open} onClose={() => setResetConfirm({ open: false, id: null })}>
                <DialogTitle>Resetear Memoria de la Regla</DialogTitle>
                <DialogContent><DialogContentText>¿Estás seguro de que quieres borrar todos los placements bloqueados para esta regla? Esta acción es irreversible.</DialogContentText></DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetConfirm({ open: false, id: null })}>Cancelar</Button>
                    <Button onClick={handleResetMemory} color="warning" variant="contained">Resetear</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

// --- COMPONENTE DEL FORMULARIO ---
const RuleForm: React.FC<{
    open: boolean;
    onClose: () => void;
    onSave: (rule: Partial<OptimizationRule>) => void;
    rule: Partial<OptimizationRule> | null;
    campaigns: Campaign[];
    trafficSources: TrafficSource[];
    connections: ApiConnection[];
    adapterManifests: AdapterManifest[];
}> = ({ open, onClose, onSave, rule, campaigns, trafficSources, connections, adapterManifests }) => {
    const [formData, setFormData] = useState<Partial<OptimizationRule>>({});

    useEffect(() => {
        if (rule) {
            setFormData(rule);
        }
    }, [rule]);

    const handleChange = (field: keyof OptimizationRule, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleConditionChange = (index: number, field: keyof RuleCondition, value: string) => {
        const newConditions = [...(formData.conditions || [])];
        newConditions[index] = { ...newConditions[index], [field]: value };
        handleChange('conditions', newConditions);
    };

    const addCondition = () => {
        const newConditions = [...(formData.conditions || []), { id: Date.now(), tracker_dimension: '', target_entity_type: '' }];
        handleChange('conditions', newConditions);
    };

    const removeCondition = (index: number) => {
        const newConditions = [...(formData.conditions || [])];
        newConditions.splice(index, 1);
        handleChange('conditions', newConditions);
    };

    const selectedCampaign = campaigns.find(c => c.id === formData.campaign_id);
    const trafficSource = trafficSources.find(ts => ts.id === selectedCampaign?.traffic_source_id);
    const connection = connections.find(c => c.id === trafficSource?.api_connection_id);
    const manifest = adapterManifests.find(m => m.key === connection?.api_adapter_key);
    const availableTargets = manifest?.supported_targets || [];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{formData?.id ? 'Editar Regla de Optimización' : 'Crear Nueva Regla'}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ pt: 2 }}>
                    <TextField label="Nombre de la Regla" value={formData.name || ''} onChange={(e) => handleChange('name', e.target.value)} fullWidth />
                    <Autocomplete
                        options={campaigns}
                        getOptionLabel={(option) => `(${option.id}) ${option.name}`}
                        value={campaigns.find(c => c.id === formData.campaign_id) || null}
                        onChange={(event, newValue) => handleChange('campaign_id', newValue ? newValue.id : '')}
                        renderInput={(params) => <TextField {...params} label="Campaña a Optimizar" />}
                    />
                    <TextField label="Umbral de Beneficio Negativo" type="number" value={formData.profit_threshold || ''} onChange={(e) => handleChange('profit_threshold', parseFloat(e.target.value))} helperText="Ej: -3.50. La regla se activará si el beneficio es MENOR a este número." InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                    
                    <Divider>CONDICIONES DE BLOQUEO</Divider>
                    
                    {formData.conditions?.map((condition, index) => (
                        <Paper key={condition.id || index} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Typography>Si la dimensión</Typography>
                                <FormControl sx={{ minWidth: 200 }}>
                                    <InputLabel>del Tracker</InputLabel>
                                    <Select value={condition.tracker_dimension || ''} label="del Tracker" onChange={(e) => handleConditionChange(index, 'tracker_dimension', e.target.value)}>
                                        {TRACKER_DIMENSIONS.map(dim => <MenuItem key={dim} value={dim}>{dim}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Typography>rinde mal, entonces bloquea la entidad</Typography>
                                <FormControl sx={{ minWidth: 200 }} disabled={!selectedCampaign}>
                                    <InputLabel>de la Red de Tráfico</InputLabel>
                                    <Select value={condition.target_entity_type || ''} label="de la Red de Tráfico" onChange={(e) => handleConditionChange(index, 'target_entity_type', e.target.value)}>
                                        {availableTargets.map(target => <MenuItem key={target.key} value={target.key}>{target.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <IconButton onClick={() => removeCondition(index)} color="error"><DeleteIcon /></IconButton>
                            </Stack>
                        </Paper>
                    ))}
                    <Button startIcon={<AddCircleIcon />} onClick={addCondition}>Añadir Condición</Button>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={() => onSave(formData)} variant="contained">Guardar Regla</Button>
            </DialogActions>
        </Dialog>
    );
};

// --- COMPONENTE DE LA TABLA ---
const RulesTable: React.FC<{
    rules: OptimizationRule[];
    onEdit: (rule: OptimizationRule) => void;
    onDelete: (id: number) => void;
    onToggleStatus: (id: number) => void;
    onResetMemory: (id: number) => void;
}> = ({ rules, onEdit, onDelete, onToggleStatus, onResetMemory }) => {
    const theme = useTheme();
    const headerCellStyle = { fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.1) };

    return (
        <Paper elevation={3} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellStyle}>Estado</TableCell>
                            <TableCell sx={headerCellStyle}>Nombre de la Regla</TableCell>
                            <TableCell sx={headerCellStyle}>Campaña</TableCell>
                            <TableCell sx={headerCellStyle}>Condiciones</TableCell>
                            <TableCell sx={headerCellStyle}>Última Ejecución</TableCell>
                            <TableCell align="right" sx={headerCellStyle}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rules.map((rule) => (
                            <TableRow key={rule.id} hover>
                                <TableCell>
                                    <Tooltip title={rule.is_active ? 'Pausar Regla' : 'Activar Regla'}>
                                        <IconButton onClick={() => onToggleStatus(rule.id)} color={rule.is_active ? 'success' : 'default'}>
                                            {rule.is_active ? <PlayCircleOutlineIcon /> : <PauseCircleOutlineIcon />}
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" fontWeight="bold">{rule.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">Profit &lt; ${rule.profit_threshold}</Typography>
                                </TableCell>
                                <TableCell>{rule.campaign_name || `ID: ${rule.campaign_id}`}</TableCell>
                                <TableCell>
                                    {rule.conditions.map(c => (
                                        <Chip key={c.tracker_dimension} size="small" label={`${c.tracker_dimension} → ${c.target_entity_type}`} sx={{ mr: 0.5, mb: 0.5 }}/>
                                    ))}
                                </TableCell>
                                <TableCell>{rule.last_run ? new Date(rule.last_run).toLocaleString() : 'Nunca'}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Resetear Memoria"><IconButton onClick={() => onResetMemory(rule.id)} color="warning"><RestartAltIcon /></IconButton></Tooltip>
                                    <Tooltip title="Editar"><IconButton onClick={() => onEdit(rule)}><EditIcon /></IconButton></Tooltip>
                                    <Tooltip title="Eliminar"><IconButton onClick={() => onDelete(rule.id)} color="error"><DeleteIcon /></IconButton></Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};


export default OptimizerPage;
